// Pragathi sidebar menu.
//
//   section("NAME")                 -> small section label
//   { title, to, icon, moduleKey }  -> menu link with an icon
//   { title, icon, content: [...] } -> group that opens to show its links
//
// `icon` is a name from the ICONS list in SideBar.js (no icon font needed).
// Routes (`to`) and permission keys (`moduleKey`) are unchanged.
// Groups and sections have no permission of their own; they show when at
// least one link inside them is visible to the user.

const section = (title) => ({ title, classsChange: "menu-title" });

export const SUPERADMIN_REPORT_KEYS = Object.freeze([
  "panchayath_master_report",
  "panchayath_monthly_tracker",
  "panchayath_subscription_summary_report",
  "billing_receipt_report",
  "panchayath_accounts_report",
]);

export const MenuList = [
  // ================= DASHBOARD =================
  section("DASHBOARD"),
  { title: "Dashboard", to: "/dashboard", icon: "dashboard", moduleKey: "dashboard" },
  { title: "Account Dashboard", to: "/account-dashboard", icon: "chart", moduleKey: "account_dashboard" },

  // ================= HOUSE MANAGEMENT =================
  section("HOUSE MANAGEMENT"),
  {
    title: "House Master",
    icon: "home",
    content: [
      { title: "Manage House", to: "/house-master", moduleKey: "house_master" },
      { title: "Bulk Import", to: "/house-master/bulkimport", moduleKey: "house_master_import" },
    ],
  },
  { title: "Wards", to: "/wards", icon: "layers", moduleKey: "wards" },
  { title: "Agents", to: "/agents", icon: "users", moduleKey: "agents" },

  // ================= FINANCE =================
  section("FINANCE"),
  { title: "Income Entry", to: "/income-entry", icon: "income", moduleKey: "income_entry" },
  { title: "Expense Entry", to: "/expense-entry", icon: "expense", moduleKey: "expense_entry" },
  { title: "Panchayath Billing", to: "/settings/panchayath-billing", icon: "receipt", moduleKey: "panchayath_billing" },

  // ================= APPROVALS =================
  section("APPROVALS"),
  { title: "Connection Approvals", to: "/house-connection-approvals", icon: "checkSquare", moduleKey: "house_connection_approvals" },
  { title: "Receipt Approvals", to: "/settings/receipt-approvals", icon: "receiptCheck", moduleKey: "receipt_approvals" },

  // ================= REPORTS =================
  section("REPORTS"),
  {
    title: "User Reports",
    icon: "report",
    content: [
      { title: "House List", to: "/reports/house-list", moduleKey: "house_list_report" },
      { title: "Receipt Reports", to: "/reports/receipt", moduleKey: "receipt_reports" },
      { title: "Water Receipt Reports", to: "/reports/receipt-water", moduleKey: "water_receipt_reports" },
      { title: "Slrm Receipt Reports", to: "/reports/receipt-slrm", moduleKey: "slrm_receipt_reports" },
      { title: "Water Bill Reports", to: "/reports/water-bill", moduleKey: "water_bill_reports" },
      { title: "SLRM Bill Reports", to: "/reports/slrm-bill", moduleKey: "slrm_bill_reports" },
      { title: "Zero Bill Reports", to: "/reports/zero-bill", moduleKey: "zero_bill_reports" },
      { title: "Balance Reports", to: "/reports/balance", moduleKey: "balance_reports" },
      { title: "Disconnection List", to: "/reports/disconnection", moduleKey: "disconnection_list" },
      { title: "Pending Reports", to: "/reports/pending", moduleKey: "pending_reports" },
      { title: "Water DCB", to: "/reports/water-dcb", moduleKey: "water_dcb" },
      { title: "SLRM DCB", to: "/reports/slrm-dcb", moduleKey: "slrm_dcb" },
      { title: "House Summary Report", to: "/reports/house-summary", moduleKey: "house_summary_report" },
      { title: "Waste Coverage Report", to: "/reports/waste-coverage", moduleKey: "waste_coverage_report" },
      { title: "GP Income & Expense Report", to: "/reports/gp-income-expense", moduleKey: "gp_income_expense_report" },
      { title: "Income Reports", to: "/reports/income-report", moduleKey: "income_reports" },
      { title: "Expense Reports", to: "/reports/expense-report", moduleKey: "expense_reports" },
      { title: "Overall Summary Report", to: "/reports/overall-summary", moduleKey: "overall_summary_report" },
    ],
  },
  {
    title: "Superadmin Reports",
    icon: "shield",
    superadminOnly: true,
    content: [
      { title: "Panchayath Master Report", to: "/reports/panchayath-master", moduleKey: "panchayath_master_report" },
      { title: "Monthly Tracker", to: "/reports/panchayath-monthly-tracker", moduleKey: "panchayath_monthly_tracker" },
      { title: "Panchayath Summary Report", to: "/reports/panchayath-summary", moduleKey: "panchayath_subscription_summary_report" },
      { title: "Billing & Receipt Report", to: "/reports/billing-receipt", moduleKey: "billing_receipt_report" },
      { title: "Panchayath Accounts Report", to: "/reports/panchayath-accounts", moduleKey: "panchayath_accounts_report" },
    ],
  },

  // ================= ADMINISTRATION =================
  section("ADMINISTRATION"),
  { title: "Manage Panchayath", to: "/settings/panchayath-master", icon: "bank", moduleKey: "panchayath_master" },
  { title: "Manage User", to: "/manage_user", icon: "userCog", moduleKey: "manage_user" },
  { title: "Assign Panchayath", to: "/settings/assign-panchayaths", icon: "link", moduleKey: "assign_panchayaths" },
  { title: "User Permissions", to: "/user-permissions", icon: "key", moduleKey: "user_permissions" },
  {
    title: "Masters",
    icon: "database",
    content: [
      { title: "Sales Executive", to: "/masters/sales-executives", moduleKey: "sales_executives" },
      { title: "District Master", to: "/district-master", moduleKey: "district_master" },
      { title: "Taluk Master", to: "/taluk-master", moduleKey: "taluk_master" },
      { title: "Income Source Master", to: "/income-sources", moduleKey: "income_source_master" },
      { title: "Expense Type Master", to: "/expense-types", moduleKey: "expense_type_master" },
      { title: "Waste Type Master", to: "/masters/waste-types", moduleKey: "waste_types" },
    ],
  },

  // ================= SETTINGS =================
  section("SETTINGS"),
  {
    title: "Settings",
    icon: "settings",
    content: [
      { title: "Financial Year", to: "/settings/financial-year", moduleKey: "financial_year" },
      { title: "Water Slrm Rate Settings", to: "/settings/waterbill-settings", moduleKey: "waterbill_settings" },
      { title: "General Settings", to: "/settings/general-settings", moduleKey: "general_settings" },
      { title: "Billing Configuration", to: "/settings/billing-configuration", moduleKey: "billing_configuration" },
      { title: "Change Password", to: "/settings/change-password", moduleKey: "change_password" },
      { title: "SMS Gateway", to: "/settings/sms-gateway", moduleKey: "sms_gateway" },
      { title: "Backup Database", to: "/settings/backup-database", moduleKey: "backup_database" },
    ],
  },
];

// Development guard: catch duplicate permission keys early.
if (process.env.NODE_ENV !== "production") {
  const keys = [];
  const walk = (items) => items.forEach((item) => {
    if (Array.isArray(item.content)) walk(item.content);
    else if (item.moduleKey) keys.push(item.moduleKey);
  });
  walk(MenuList);
  const duplicates = keys.filter((key, i) => keys.indexOf(key) !== i);
  if (duplicates.length) {
    // eslint-disable-next-line no-console
    console.error(`Pragathi sidebar: duplicate moduleKey(s): ${duplicates.join(", ")}`);
  }
}
